import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import BrandLockup from "@/components/BrandLockup";
import { useAuth } from "@/contexts/AuthContext";
import { clearAuthAttempt, createAuthAttemptId, rememberAuthAttempt } from "@/lib/authAttempt";
import { normalizeEmailOtp } from "@/lib/emailOtp";
import { buildAuthCallbackUrl, normalizeReturnPath } from "@/lib/authRedirect";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import {
  supabase,
  supabaseConfigError,
  supabaseConfigWarning,
} from "@/lib/supabase";

export default function Login() {
  const [, navigate] = useLocation();
  const { session, isLoading, isConfigured } = useAuth();
  const requestedPath = new URLSearchParams(window.location.search).get("next");
  const returnPath = normalizeReturnPath(requestedPath);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completingOtpRef = useRef(false);

  useEffect(() => {
    if (!isLoading && session && !completingOtpRef.current) {
      navigate(returnPath, { replace: true });
    }
  }, [isLoading, navigate, returnPath, session]);

  async function requestEmailSignIn(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setError(null);
    setCode("");
    if (!supabase) {
      setError("Sign-in isn’t available yet. Please try again later.");
      return;
    }

    setIsSending(true);
    let attemptId: string | null = null;
    try {
      attemptId = createAuthAttemptId();
      const stored = rememberAuthAttempt({
        id: attemptId,
        returnPath,
        createdAt: Date.now(),
      });
      if (!stored) {
        throw new Error("Please allow site storage in this browser so we can finish sign-in on the same device.");
      }
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          emailRedirectTo: buildAuthCallbackUrl(returnPath, window.location.origin, attemptId),
        },
      });
      if (signInError) throw signInError;
      setAttemptId(attemptId);
      setSent(true);
    } catch (requestError) {
      clearAuthAttempt(attemptId);
      setError(friendlyErrorMessage(requestError, "sign-in"));
    } finally {
      setIsSending(false);
    }
  }

  async function verifyEmailCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const token = normalizeEmailOtp(code);
    if (token.length !== 6) {
      setError("Enter all 6 digits from the email.");
      return;
    }
    if (!supabase) {
      setError("Sign-in isn’t available yet. Please try again later.");
      return;
    }
    if (!attemptId) {
      setError("Please request a new sign-in email before entering a code.");
      return;
    }

    setIsVerifying(true);
    completingOtpRef.current = true;
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token,
        type: "email",
      });
      if (verifyError) throw verifyError;
      if (!data.session) throw new Error("That code did not create a sign-in session. Please request a new email.");
      const callbackUrl = new URL(buildAuthCallbackUrl(returnPath, window.location.origin, attemptId));
      navigate(`${callbackUrl.pathname}${callbackUrl.search}`, { replace: true });
    } catch (verifyError) {
      completingOtpRef.current = false;
      setError(friendlyErrorMessage(verifyError, "code"));
    } finally {
      setIsVerifying(false);
    }
  }

  function useDifferentEmail() {
    clearAuthAttempt(attemptId);
    setAttemptId(null);
    setSent(false);
    setCode("");
    setError(null);
  }

  return (
    <div className="paper-shell inner-shell signin-shell">
      <header className="site-header">
        <BrandLockup />
      </header>

      <main className="form-layout">
        <div className="form-intro">
          <p className="eyebrow"><span className="eyebrow-rule" /> Sign in</p>
          <h1>Welcome<span className="title-period">.</span></h1>
        </div>

        <section className="form-card" aria-labelledby="signin-title">
          {sent ? (
            <div className="confirmation-state" role="status" aria-live="polite">
              <div className="confirmation-seal" aria-hidden="true">✉</div>
              <h2 id="signin-title">Check your email</h2>
              <p>We sent a code to <strong>{email.trim()}</strong>. Enter it below.</p>
              <form className="otp-form" onSubmit={verifyEmailCode}>
                <label className="field-label" htmlFor="email-code">Enter the 6-digit code</label>
                <input
                  className="email-input otp-input"
                  id="email-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(event) => setCode(normalizeEmailOtp(event.target.value))}
                  aria-describedby={error ? "signin-error" : undefined}
                  required
                />
                {error && <p className="error-message" id="signin-error" role="alert">{error}</p>}
                <button className="primary-action full-width" type="submit" disabled={isVerifying || !isConfigured}>
                  {isVerifying ? "Checking your code…" : "Verify code"}
                  {!isVerifying && <span aria-hidden="true">→</span>}
                </button>
              </form>
              <button className="secondary-action resend-email" type="button" onClick={() => void requestEmailSignIn()} disabled={isSending || !isConfigured}>
                {isSending ? "Sending another code…" : "Send another code"}
              </button>
              <button className="claim-skip" type="button" onClick={useDifferentEmail}>Use a different email</button>
            </div>
          ) : (
            <form onSubmit={(event) => void requestEmailSignIn(event)} noValidate={false}>
              <h2 id="signin-title">Sign in with email</h2>
              <label className="field-label" htmlFor="email">Your email address</label>
              <input
                className="email-input"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                aria-describedby={error ? "signin-error" : "email-help"}
              />
              <p className="field-help" id="email-help">We’ll email you a 6-digit code. No password needed.</p>
              {error && <p className="error-message" id="signin-error" role="alert">{error}</p>}
              {!isConfigured && (
                <p className="setup-message" role="status">
                  {supabaseConfigError ?? "Supabase settings are needed before email sign-in can work."}
                </p>
              )}
              {isConfigured && supabaseConfigWarning && (
                <p className="setup-message" role="status">{supabaseConfigWarning}</p>
              )}
              <button className="primary-action full-width" type="submit" disabled={isSending || !isConfigured}>
                {isSending ? "Sending your email…" : "Send sign-in email"}
                {!isSending && <span aria-hidden="true">→</span>}
              </button>
            </form>
          )}
        </section>
        <p className="signin-consent">
          By continuing you agree to our <Link href="/terms">Terms</Link> and <Link href="/privacy">Privacy Policy</Link>
        </p>
      </main>

      <footer className="site-footer signin-site-footer">
        <Link href="/" className="back-link">← Back to Home, Twice</Link>
        <nav className="legal-footer-links" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <Link href="/terms">Terms</Link>
        </nav>
        <span>Published by Kwaderno &amp; Co.</span>
      </footer>
    </div>
  );
}
